import {Component, Input, OnInit} from "@angular/core";
import {
  AppState, selectBookedShipments, selectDeliveredShipments, selectOngoingShipments, selectRouterState,
  selectSavedShipments
} from "@store/reducers";
import {FontAwesomeService} from "@services/font-awesome.service";
import {Router} from "@angular/router";
import {Store} from "@ngrx/store";
import {takeUntil} from "rxjs/operators";
import {SetFilteredShipments} from "@store/actions";
import {Subject} from "rxjs/Subject";
import {Shipment} from "@models/shipment.interface";
import {ShipmentTypes} from "@store/reducers/shipments.reducer";
import {ShipmentPageLoad} from "@store/actions/shipments.action";
import {AccountService} from "@services/account.service";

@Component({
  selector: 'app-shipment-list',
  template: `
      <div *ngIf="currentShipmentType === 'SAVED'">
        <app-saved-shipments 
          [data]="shipmentsState?.activeSearch ? filteredShipments : shipments"
          [totalShipmentsLength]="filteredShipmentsLength || totalShipmentsLength"
          (onChangePage)="handleChangePage($event)"
          [shipmentsState]="shipmentsState"
          [currentPage]="currentPage"
          [currentPageSize]="currentPageSize">
        </app-saved-shipments>
      </div>
      <div *ngIf="currentShipmentType === 'BOOKED'">
        <app-booked-shipments
          [data]="shipmentsState?.activeSearch ? filteredShipments : shipments"
          [totalShipmentsLength]="filteredShipmentsLength || totalShipmentsLength"
          (onChangePage)="handleChangePage($event)"
          [shipmentsState]="shipmentsState"
          [currentPage]="currentPage"
          [currentPageSize]="currentPageSize"></app-booked-shipments>
      </div>
      <div *ngIf="currentShipmentType === 'ONGOING'">
        <app-ongoing-shipments
          [data]="shipmentsState?.activeSearch ? filteredShipments : shipments"
          [totalShipmentsLength]="filteredShipmentsLength || totalShipmentsLength"
          (onChangePage)="handleChangePage($event)"
          [shipmentsState]="shipmentsState"
          [currentPage]="currentPage"
          [currentPageSize]="currentPageSize">
        </app-ongoing-shipments>
      </div>
      <div *ngIf="currentShipmentType === 'DELIVERED'">
        <app-delivered-shipments
          [data]="shipmentsState?.activeSearch ? filteredShipments : shipments"
          [totalShipmentsLength]="filteredShipmentsLength || totalShipmentsLength"
          (onChangePage)="handleChangePage($event)"
          [shipmentsState]="shipmentsState"
          [currentPage]="currentPage"
          [currentPageSize]="currentPageSize">
        </app-delivered-shipments>
      </div>
  `
})

export class ShipmentListComponent implements OnInit {
  public currentShipmentType: string;
  public shipmentsState: any;
  public currentPage: number = 1;
  public currentPageSize: number = 25;
  public shipments: Shipment[];
  public filteredShipments: Shipment[];
  public filteredShipmentsLength: number;
  public totalShipmentsLength: number;

  private unsubscribe: Subject<void> = new Subject();
  private shipmentTypes = ShipmentTypes;

  constructor(
    private store: Store<AppState>,
    public faService: FontAwesomeService,
    private router: Router,
    private accountService: AccountService,
  ) {}

  ngOnInit() {
    this.store.select(selectRouterState)
      .subscribe(data => {
        const url = data.state.url.split('/').pop();
        let observable;
        if (url.match('saved')) {
          this.currentShipmentType = 'SAVED';
          observable = this.store.select(selectSavedShipments);
        } else if (url.match('booked')) {
          this.currentShipmentType = 'BOOKED';
          observable = this.store.select(selectBookedShipments);
        } else if (url.match('ongoing')) {
          this.currentShipmentType = 'ONGOING';
          observable = this.store.select(selectOngoingShipments);
        } else if (url.match('delivered')) {
          observable = this.store.select(selectDeliveredShipments);
          this.currentShipmentType = 'DELIVERED';
        }
        if (!observable) {
          return;
        }
        observable
          .pipe(takeUntil(this.unsubscribe))
          .subscribe(shipments => {
            this.shipmentsState = shipments;
            if (this.shipmentsState && this.shipmentsState.current_page !== this.currentPage) {
              this.currentPage = this.shipmentsState.current_page;
            }
            if (this.shipmentsState && this.shipmentsState.data[this.currentPageSize]) {
              this.shipments = shipments.data[this.currentPageSize][this.currentPage];
            }
            if (this.shipmentsState &&
              this.shipmentsState.activeSearch &&
              this.shipmentsState.filteredData &&
              this.shipmentsState.filteredData[this.shipmentsState.activeSearch]) {
              this.filteredShipments =
                this.shipmentsState.filteredData[this.shipmentsState.activeSearch].data[this.currentPageSize][this.currentPage];
              this.filteredShipmentsLength = this.shipmentsState.filteredData[this.shipmentsState.activeSearch].total;
            } else {
              this.filteredShipments = [];
              this.filteredShipmentsLength = 0;
            }
          });

        this.fetchShipments(this.currentPage);
      });
  }

  handleChangePage(current_page) {
    this.currentPage = current_page;
    if (this.shipmentsState &&
      this.shipmentsState.activeSearch &&
      this.shipmentsState.filteredData &&
      this.shipmentsState.filteredData[this.shipmentsState.activeSearch] &&
      this.shipmentsState.filteredData[this.shipmentsState.activeSearch].data &&
      this.shipmentsState.filteredData[this.shipmentsState.activeSearch].data[this.currentPageSize] &&
      this.shipmentsState.filteredData[this.shipmentsState.activeSearch].data[this.currentPageSize][this.currentPage]
    ) {
      this.filteredShipments =
        this.shipmentsState.filteredData[this.shipmentsState.activeSearch].data[this.currentPageSize][this.currentPage];
      this.filteredShipmentsLength = this.shipmentsState.total;
      return;
    }

    if (
      this.shipmentsState &&
      this.shipmentsState.data[this.currentPageSize][this.currentPage] &&
      !this.shipmentsState.activeSearch) {
      this.shipments = this.shipmentsState.data[this.currentPageSize][this.currentPage];
      this.totalShipmentsLength = this.shipmentsState.total;
      return;
    }
    this.fetchShipments(current_page);
  }

  fetchShipments(current_page) {
    let params: any = {
      size: this.currentPageSize,
      page: current_page,
    };
    if (this.shipmentsState && this.shipmentsState.activeSearch) {
      params[this.currentShipmentType === 'SAVED' ? 'search' : 'shipment_ref'] = this.shipmentsState.activeSearch;
    }
    if (this.currentShipmentType === 'BOOKED') {
      params.statuses = ['CREATED', 'STORED', 'PRE_TRANSPORT', 'CONFIRMED'];
    }

    if (this.currentShipmentType === 'DELIVERED') {
      params.statuses = ['DELIVERED'];
    }

    if (this.currentShipmentType === 'ONGOING') {
      params.statuses = ['IN_TRANSIT', 'PICKUP', 'EXCEPTION', 'DESTINATION_HANDLING', 'FAILED'];
    }
    const observable = this.currentShipmentType === 'SAVED' ? this.accountService.getSearchDraft(params) :
      this.accountService.getShipments(params);
    if (!observable) {
      return;
    }

    observable
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => {
        if (this.shipmentsState && this.shipmentsState.activeSearch) {
          this.filteredShipmentsLength = data.total;
          this.store.dispatch(new SetFilteredShipments({
            type: this.shipmentTypes[this.currentShipmentType],
            activeSearch: this.shipmentsState.activeSearch,
            data: data.data,
            current_page_size: this.currentPageSize,
            current_page: this.currentPage,
            total: data.total
          }));
        } else {
          this.totalShipmentsLength = data.total;
          const shipments = {
            type: this.shipmentTypes[this.currentShipmentType],
            current_page_size: this.currentPageSize,
            current_page,
            total: data.total,
            data: data.data,
          };
          this.store.dispatch(new ShipmentPageLoad(shipments));
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/shipments/components/shipments-list/shipment-list.component.ts